import React, { useEffect, useState } from "react";
import CustomDateRangePicker from "./CustomDateRangePicker";
import CustomListing from "./CustomListing";
import CustomRoomSelect from "./CustomRoomSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  setCheckIn,
  setCheckOut,
  setHotelName,
  setRooms,
  setSearchName,
} from "../redux/website/search/SearchSlice";
import { useNavigate } from "react-router";

const STORAGE_KEY = "@dhoorbin:hotelSearch";
const readSearch = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeSearch = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / private mode */
  }
};

const HotelSearch = () => {
  const navigate = useNavigate();
  const Rooms = useSelector((state) => state.search.room);

  const dispatch = useDispatch();

  const [Room, setRoom] = useState([{ room: 1, adult: 2, children: 0 }]);
  // Locations is either a city row
  //   { cityId, cityName, divisionId, divisionName, countryId, countryName }
  // or a hotel row { hotelId, hotelName, hotelLogo }, or "" (default).
  const [Locations, setLocations] = useState("");

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 5);
  const weekLater = new Date();
  weekLater.setDate(today.getDate() + 6);

  const [range, setRange] = useState({
    start: tomorrow.toLocaleDateString("en-CA"),
    end: weekLater.toLocaleDateString("en-CA"),
  });
  const [IshowCalendar, setIshowCalendar] = useState(false);
  const [IsShowSearch, setIsShowSearch] = useState(false);
  const [IsShowRoom, setIsShowRoom] = useState(false);

  // Hydrate from sessionStorage once, so coming back keeps the last search.
  useEffect(() => {
    const saved = readSearch();
    if (!saved) return;

    if (saved.checkIn && saved.checkOut) {
      setRange({ start: saved.checkIn, end: saved.checkOut });
    }
    if (Array.isArray(saved.roomList) && saved.roomList.length > 0) {
      setRoom(saved.roomList);
    }
    // rebuild the selection object for the picker/display
    if (saved.hotelName) {
      setLocations({
        hotelId: saved.hotelId,
        hotelName: saved.hotelName,
      });
    } else if (saved.city) {
      setLocations({
        cityId: saved.cityId,
        cityName: saved.city,
        divisionId: saved.divisionId,
        divisionName: saved.division,
        countryName: saved.country,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowCalendar = () => {
    setIshowCalendar(!IshowCalendar);
    setIsShowSearch(false);
    setIsShowRoom(false);
  };
  const handleShowSearch = () => {
    setIsShowSearch(!IsShowSearch);
    setIshowCalendar(false);
    setIsShowRoom(false);
  };
  const handleShowRoom = () => {
    setIsShowRoom(!IsShowRoom);
    setIshowCalendar(false);
    setIsShowSearch(false);
  };
  const TotalRoom = Rooms.reduce((total) => total + 1, 0);
  const TotalGuest = Rooms.reduce(
    (total, room) => total + room.adult + room.children,
    0,
  );
  const totalAdult = Rooms.reduce((total, room) => total + room.adult, 0);
  const totalChildren = Rooms.reduce((total, room) => total + room.children, 0);

  // is the current selection a hotel?
  const isHotel = Locations && Locations.hotelName;

  useEffect(() => {
    dispatch(setRooms(Room));
    dispatch(setCheckIn(range.start));
    dispatch(setCheckOut(range.end));
  }, [Room, dispatch, range]);

  // Keep redux in sync with the local selection.
  useEffect(() => {
    if (Locations !== "") {
      setIsShowSearch(false);
    }
    if (isHotel) {
      dispatch(setHotelName(Locations.hotelName || ""));
      dispatch(setSearchName(""));
    } else if (Locations && Locations.cityName) {
      dispatch(setSearchName(Locations.cityName || ""));
      dispatch(setHotelName(""));
    }
  }, [Locations, dispatch, isHotel]);

  const handleSearch = () => {
    // Don't search until an end date is chosen
    if (!range.end) {
      setIshowCalendar(true);
      return;
    }

    // Build a flat params object (same shape as the URL params) and reuse it
    // for the URL, redux and sessionStorage so all three stay in sync.
    const data = {
      checkIn: range.start,
      checkOut: range.end,
      room: TotalRoom,
      guest: TotalGuest,
      adult: totalAdult,
      children: totalChildren,
      roomList: Room, // keep the full room config for hydration
    };

    if (isHotel) {
      data.hotelName = Locations.hotelName;
      if (Locations.hotelId) data.hotelId = Locations.hotelId;
    } else if (Locations && Locations.cityName) {
      data.city = Locations.cityName;
      if (Locations.cityId) data.cityId = Locations.cityId;
      if (Locations.divisionName) data.division = Locations.divisionName;
      if (Locations.divisionId) data.divisionId = Locations.divisionId;
      if (Locations.countryName) data.country = Locations.countryName;
    } else {
      data.city = "Cox's Bazar";
    }

    // persist
    writeSearch(data);

    // build URL params from the same object (skip roomList)
    const params = new URLSearchParams();
    Object.entries(data).forEach(([k, v]) => {
      if (k === "roomList" || v === undefined || v === null || v === "") return;
      params.set(k, v);
    });

    navigate(`/product/search?${params.toString()}`);
  };
  return (
    <div
      className="
  absolute top-44 lg:top-8
  ms:left-1/2 mm:left-1/2 ml:left-1/2
  ms:-translate-x-1/2 mm:-translate-x-1/2 ml:-translate-x-1/2
  lg:right-0 lg:left-auto lg:translate-x-0
  border px-4 py-4 h-auto
  w-[320px] ms:w-[270px] mm:w-[320px] ml:w-[350px] lg:w-fit
  bg-white z-10 rounded-2xl shadow-lg
"
    >
      <div className="flex flex-col items-start gap-y-4">
        <div className="relative w-full">
          <button
            onClick={handleShowSearch}
            className="space-y-1 border w-full p-2 text-start rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-sm">City | Hotel | Resort</p>
            <p className="text-md lg:text-xl uppercase font-extrabold text-[#2155ad]">
              {isHotel && Locations.hotelName}
              {!isHotel && Locations.cityName && Locations.cityName}
              {Locations === "" && "Cox's Bazar"}
            </p>
            <p className="text-black/50 text-xs">
              {!isHotel && Locations.countryName}
              {Locations === "" && "Bangladesh"}
            </p>
          </button>
          {IsShowSearch && (
            <div className="absolute z-30 top-24 left-1/2 border rounded-lg transform -translate-x-1/2 shadow-lg w-full">
              <CustomListing
                Locations={Locations}
                setLocations={setLocations}
              />
            </div>
          )}
        </div>

        <div className="flex space-x-4 w-full">
          <button
            onClick={handleShowCalendar}
            className="space-y-1 border text-start w-full lg:w-[220px] p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-sm ">Check In</p>
            <p
              onClick={handleShowCalendar}
              className="text-md lg:text-xl uppercase font-extrabold text-[#2155ad]"
            >
              {new Date(range.start).toLocaleString("en-US", {
                month: "short",
              })}
              '{new Date(range.start).getDate()}
            </p>
            <p className="text-black/50 text-xs">
              {" "}
              {new Date(range.start).toLocaleString("en-US", {
                weekday: "long",
              })}
            </p>
          </button>
          <button
            onClick={handleShowCalendar}
            className="space-y-1 border w-full lg:w-[220px] text-start p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-sm ">Check Out</p>
            {range.end ? (
              <>
                <p
                  onClick={handleShowCalendar}
                  className="text-md lg:text-xl uppercase font-extrabold text-[#2155ad]"
                >
                  {new Date(range.end).toLocaleString("en-US", {
                    month: "short",
                  })}
                  '{new Date(range.end).getDate()}
                </p>
                <p className="text-black/50 text-xs">
                  {" "}
                  {new Date(range.end).toLocaleString("en-US", {
                    weekday: "long",
                  })}
                </p>
              </>
            ) : (
              <p className="text-xs text-black/50 mt-2">
                Select end date to continue
              </p>
            )}
          </button>
        </div>
        {IshowCalendar && (
          <div className="absolute z-30 top-56 left-1/2 transform -translate-x-1/2">
            <CustomDateRangePicker
              setIshowCalendar={setIshowCalendar}
              setRange={setRange}
              range={range}
            />
          </div>
        )}
        <div className="relative w-full">
          <button
            onClick={handleShowRoom}
            className="space-y-1 border text-start w-full p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-sm ">Rooms and Guests</p>
            <p className="text-md lg:text-xl uppercase font-extrabold text-[#2155ad]">
              {TotalRoom} Room | {TotalGuest} Guests
            </p>
            <p className="text-black/50 text-xs">
              {totalAdult} Adults, {totalChildren} children
            </p>
          </button>
          {IsShowRoom && (
            <div className="absolute z-30 top-24 left-2/4 transform -translate-x-1/2">
              <CustomRoomSelect Room={Room} setRoom={setRoom} />
            </div>
          )}
        </div>
        <div className="w-full">
          <button
            onClick={handleSearch}
            className="px-12 w-full py-2 rounded-lg text-white bg-[#0a1d46]"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

export default HotelSearch;
